import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import Cookies from 'universal-cookie';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Facebook from '../Facebook';
import UserImage from '../UserImage/UserImage';
import SubUserAdd from '../SubUserAdd';
import MyAccountUpdateDetailsModal from './MyAccountUpdateDetailsModal';
import MyAccountManageSubscriptionModal from './MyAccountManageSubscriptionModal';
import StudioOverviewMetrics from './StudioOverviewMetrics';
import api from '../../data/api';
import { onboardingV2Api } from '../../data/api/onboardingV2';
import axiosConfig from '../../data/utils/axiosConfigInstance';
import { fileUploadApi } from '../../data/api/fileUpload';
import { userInfoAtom } from '../../data/atoms/utilsAtoms';
import { setUserInfo } from '../../redux/authSlice';
import Toast from '../../utils/toastManager';
import VideoCardV2 from '../VideoCardV2';
import { ContentView } from '../../Pages/VideoPlayer/utils/queryParamsKeys';
import { generatePlayerPageViewUrl } from '../../utils/generatePlayerPageViewUrl';
import './MyAccountV2.css';

const RECOMMENDED_PLAYLIST_NAME = 'Recommended For You';

const getPlaylistTimestamp = playlist => {
	const timestamp = playlist?.updatedAt || playlist?.createdAt;
	return timestamp ? new Date(timestamp).getTime() : 0;
};

const getLatestRecommendedPlaylist = playlists => {
	if (!Array.isArray(playlists)) return null;

	return playlists
		.filter(playlist => playlist?.name === RECOMMENDED_PLAYLIST_NAME && playlist?.videos?.length > 0)
		.sort((a, b) => getPlaylistTimestamp(b) - getPlaylistTimestamp(a))[0] || null;
};

const getUserTypeFromToken = token => {
	if (!token) return '';

	try {
		const payload = JSON.parse(atob(token.replace(/"/g, '').split('.')[1]));
		return payload?.userType || '';
	} catch (error) {
		return '';
	}
};

function MyAccountV2() {
	const searchParams = new URLSearchParams(window.location.search);
	const success = searchParams.get('success');

	if (success === 'true') {
		window?.fbq('trackCustom', 'WebApp_SignUp_ChargebeeCompleted');
		window?.fbq('track', 'PageView');
	}

	const redirect = useNavigate();
	const dispatch = useDispatch();
	const cookies = new Cookies();
	const reduxUserInfo = useSelector(state => state.authState.userInfo);
	const [userData, setUserData] = useAtom(userInfoAtom);
	const userInfo = reduxUserInfo || userData?.user || {};
	const userId = userInfo?.userId || userData?.user?.userId;
	const tokenUserType = getUserTypeFromToken(userData?.token || localStorage.getItem('token'));
	const userTypes = [userData?.user?.userType, userInfo?.userType, tokenUserType].filter(Boolean);
	const isSubuser = userTypes.includes('subuser');
	const isStudioOwner = !isSubuser && userTypes.includes('studio-owner');
	const onboardingCompleted = userData?.user?.onboardingCompleted ?? userInfo?.onboardingCompleted;
	const shouldShowRecommended = onboardingCompleted !== true;

	const [firstname, setFirstname] = useState('');
	const [lastname, setLastname] = useState('');
	const [primaryEmail, setPrimaryEmail] = useState('');
	const [secondaryEmail, setSecondaryEmail] = useState('');
	const [passwordMask] = useState('************');
	const [isUpdatingAccount, setIsUpdatingAccount] = useState(false);
	const [subscriptionData, setSubscriptionData] = useState({});
	const [status, setStatus] = useState('');
	const [plan, setPlan] = useState('');
	const [nextBilling, setNextBilling] = useState('');
	const [method, setMethod] = useState('');
	const [card, setCard] = useState('');
	const [seatsData, setSeatsData] = useState();
	const [accountDetailsModalOpen, setAccountDetailsModalOpen] = useState(false);
	const [manageSubscriptionModalOpen, setManageSubscriptionModalOpen] = useState(false);
	const [recommendedPlaylist, setRecommendedPlaylist] = useState(null);
	const [isLoadingRecommendedPlaylist, setIsLoadingRecommendedPlaylist] = useState(false);
	const [isStartingQuiz, setIsStartingQuiz] = useState(false);

	const displayEmail = secondaryEmail || primaryEmail;

	const displayFrequency = freq => {
		switch (freq) {
			case 'month':
				return 'Monthly';
			case 'year':
				return 'Yearly';
			case 'annual':
				return 'Annual';
			default:
				return freq || 'No data Found';
		}
	};

	const displayStatus = subscriptionStatus => {
		switch (subscriptionStatus) {
			case 'active':
				return 'Active';
			case 'future':
				return 'Future';
			case 'in_trial':
				return 'In Trial';
			case 'non_renewing':
				return 'Non Renewing';
			case 'paused':
				return 'Paused';
			case 'cancelled':
				return 'Cancelled';
			default:
				return subscriptionStatus || 'No data Found';
		}
	};

	useEffect(() => {
		const findPersonalData = async () => {
			try {
				const response = await api.getPersonalInformation(userId);

				if (response.status === 200) {
					setFirstname(response?.data?.firstname || '');
					setLastname(response?.data?.lastname || '');
					setPrimaryEmail(response?.data?.email?.toLowerCase() || '');
					setUserData(prev => ({
						...prev,
						user: {
							...prev.user,
							firstname: response?.data?.firstname,
							lastname: response?.data?.lastname,
							email: response?.data?.email?.toLowerCase(),
							userType: response?.data?.userType,
							onboardingRole: response?.data?.onboardingRole ?? prev.user?.onboardingRole,
							onboardingCompleted: response?.data?.onboardingCompleted ?? prev.user?.onboardingCompleted
						}
					}));
				} else if (response.status === 201) {
					Toast.error('"Please Login Again', {
						position: 'top-center',
						duration: 3000
					});
					redirect('/login');
				}
			} catch (error) {
				if (error.response?.status === 401) {
					Toast.error(error?.response?.data?.message, {
						position: 'top-center',
						duration: 3000
					});
				}
				console.log(error);
			}
		};

		if (userId) {
			findPersonalData();
		}
	}, [redirect, setUserData, userId]);

	useEffect(() => {
		const fetchSecondaryEmail = async () => {
			try {
				const payload = await api.getSecondaryEmails();
				setSecondaryEmail(payload?.data?.secondaryEmail?.toLowerCase() || '');
			} catch (error) {
				setSecondaryEmail('');
			}
		};

		if (userId) {
			fetchSecondaryEmail();
		}
	}, [userId]);

	useEffect(() => {
		const findSubscriptionData = async () => {
			if (!userData.subscriptionId && !userData.user?.subscriptionReferenceId && !userData.user?.subscription) {
				setSubscriptionData({});
				setStatus('No active subscription');
				setPlan('No active subscription');
				setNextBilling('Not scheduled');
				setMethod('');
				setCard('');
				return;
			}

			try {
				const response = await api.getSubscriptioDetails(userId);

				if (response.status === 200) {
					setSubscriptionData(response?.data || {});
					setStatus(response?.data?.subscriptionStatus);
					setPlan(response?.data?.billing_period_unit);
					const billingTimestamp = response?.data?.next_billing_at;
					setNextBilling(billingTimestamp ? new Date(billingTimestamp * 1000).toLocaleDateString() : 'Not scheduled');
					setMethod(response?.data?.card_type || '');
					setCard(response?.data?.masked_number ? response.data.masked_number.replace(/\*/g, '') : '');
				} else if (response.status === 201) {
					Toast.error('"Please Login Again', {
						position: 'top-center',
						duration: 3000
					});
					redirect('/login');
				}
			} catch (error) {
				if (error.response?.status === 401) {
					Toast.error(error.response.data.message, {
						position: 'top-center',
						duration: 3000
					});
				}
				setStatus('No data Found');
				setPlan('No data Found');
				setNextBilling('No data Found');
				setCard('No data Found');
				console.log(error);
			}
		};

		if (userId && !isSubuser) {
			findSubscriptionData();
		}
	}, [isSubuser, redirect, userId]);

	useEffect(() => {
		const fetchRecommendedPlaylist = async () => {
			setIsLoadingRecommendedPlaylist(true);

			try {
				const playlistRequests = [
					axiosConfig.post('/personalvideo', { userId })
						.then(response => response?.data?.existingVideos || [])
						.catch(error => {
							if (error.response?.status === 404) return [];
							throw error;
						})
				];

				if (isStudioOwner) {
					playlistRequests.push(
						axiosConfig.get('/studio-playlist/all')
							.then(response => response?.data?.data?.existingVideos || response?.data?.data?.data || [])
							.catch(error => {
								if (error.response?.status === 404) return [];
								throw error;
							})
					);
				}

				const playlistGroups = await Promise.all(playlistRequests);
				const latestPlaylist = getLatestRecommendedPlaylist(playlistGroups.flat());
				setRecommendedPlaylist(latestPlaylist);
			} catch (error) {
				console.error('Unable to fetch recommended playlist:', error);
				setRecommendedPlaylist(null);
			} finally {
				setIsLoadingRecommendedPlaylist(false);
			}
		};

		if (userId && !isSubuser && !shouldShowRecommended) {
			fetchRecommendedPlaylist();
		} else {
			setRecommendedPlaylist(null);
		}
	}, [isStudioOwner, isSubuser, shouldShowRecommended, userId]);

	const handleUpdateAccountDetails = async values => {
		const nextFirstname = values?.firstname?.trim() || '';
		const nextLastname = values?.lastname?.trim() || '';
		const nextEmail = values?.email?.trim().toLowerCase() || '';

		if (!nextFirstname || !nextLastname || !userId) {
			Toast.error('First name and last name are required.', {
				position: 'top-center',
				duration: 3000
			});
			return false;
		}

		const emailFormatRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!isSubuser && !emailFormatRegex.test(nextEmail)) {
			Toast.error('Invalid email format', {
				position: 'top-center',
				duration: 3000
			});
			return false;
		}

		setIsUpdatingAccount(true);

		try {
			const nameResponse = await api.UpdatePersonalInformationName(nextFirstname, nextLastname, userId);

			if (nameResponse.status === 201) {
				Toast.error('"Please Login Again', {
					position: 'top-center',
					duration: 3000
				});
				redirect('/login');
				return false;
			}

			let nextSecondaryEmail = secondaryEmail || '';
			const normalizedPrimaryEmail = (primaryEmail || '').trim().toLowerCase();
			const normalizedCurrentSecondaryEmail = (secondaryEmail || '').trim().toLowerCase() || '';
			const normalizedEffectiveEmail = (normalizedCurrentSecondaryEmail || normalizedPrimaryEmail).trim();
			const normalizedNextEmail = nextEmail.trim().toLowerCase();

			if (!isSubuser && normalizedNextEmail && normalizedNextEmail !== normalizedEffectiveEmail) {
				try {
					if (normalizedCurrentSecondaryEmail && normalizedNextEmail === normalizedPrimaryEmail) {
						await api.removeSecondaryEmail(normalizedCurrentSecondaryEmail);
					} else {
						await api.addSecondaryEmail(normalizedNextEmail);
					}

					const payload = await api.getSecondaryEmails();
					nextSecondaryEmail = payload?.data?.secondaryEmail || '';
					setSecondaryEmail(nextSecondaryEmail);
				} catch (error) {
					const errorMessage = error.response?.data?.message || 'Unable to update email.';
					Toast.error(errorMessage);
					return false;
				}
			}

			const updatedEmailForDisplay = nextSecondaryEmail || primaryEmail;

			const updatedUser = {
				...userInfo,
				firstname: nextFirstname,
				lastname: nextLastname,
				email: updatedEmailForDisplay
			};

			cookies.set('user', updatedUser);
			localStorage.setItem('userData', JSON.stringify(updatedUser));
			dispatch(setUserInfo(updatedUser));
			setFirstname(nextFirstname);
			setLastname(nextLastname);
			setUserData(prev => ({
				...prev,
				user: {
					...prev.user,
					...updatedUser
				}
			}));

			Toast.success('Account details updated', {
				position: 'top-center',
				duration: 3000
			});
			setAccountDetailsModalOpen(false);
			return true;
		} catch (error) {
			if (error.response?.status === 401) {
				Toast.error(error.response.data.message, {
					position: 'top-center',
					duration: 3000
				});
			} else {
				Toast.error('Unable to update account details.', {
					position: 'top-center',
					duration: 3000
				});
			}
			return false;
		} finally {
			setIsUpdatingAccount(false);
		}
	};

	const handleSubscriptionManagementUpdate = payload => {
		if (!payload) return;
		setStatus(payload.status || '');
		setPlan(payload.billingPeriodUnit || '');
		setNextBilling(
			payload.nextBillingAt ? new Date(payload.nextBillingAt * 1000).toLocaleDateString() : 'Not scheduled'
		);
	};

	const fullName = [firstname, lastname].filter(Boolean).join(' ') || 'Your Account';
	const recommendedVideos = recommendedPlaylist?.videos || [];
	const recommendedContentView =
		recommendedPlaylist?.type === ContentView.STUDIO ? ContentView.STUDIO : ContentView.PERSONAL;

	const handleRecommendedVideoClick = video => {
		const videoId = video?._id || video?.videoDetails?._id;
		if (!videoId) return;

		redirect(generatePlayerPageViewUrl(videoId, recommendedContentView, recommendedPlaylist?._id));
	};

	const handleQuizClick = async () => {
		if (isStartingQuiz) return;

		setIsStartingQuiz(true);
		try {
			const response = await onboardingV2Api.getOnboardingRole();
			const onboardingRole = response?.data?.onboardingRole || userInfo?.onboardingRole || '';
			const planType = isStudioOwner ? 'studio' : 'standard';
			const isRetake = !shouldShowRecommended;
			const params = new URLSearchParams({
				plan: planType,
				billing: 'yearly',
				planId: '',
				role: onboardingRole
			});

			if (isRetake) {
				params.set('retake', 'true');
			}

			sessionStorage.setItem('onboarding_entry', 'account');

			redirect(`/onboarding/${onboardingRole ? 'step-2' : 'step-1'}?${params.toString()}`, {
				state: {
					from: 'account',
					planType,
					userType: isStudioOwner ? 'studio-owner' : undefined,
					onboardingRole,
					isRetake
				}
			});
		} catch (error) {
			console.error('Unable to start onboarding quiz:', error);
			Toast.error('Unable to start quiz. Please try again.');
		} finally {
			setIsStartingQuiz(false);
		}
	};

	const scrollToSection = (id) => {
		const element = document.getElementById(id);
		if (element) {
			const comparisonEl = document.querySelector('.account-v2-chips-comparison');
			const comparisonHeight = comparisonEl ? comparisonEl.offsetHeight : 50;
			const totalOffset = 90 + comparisonHeight + 20; // 90px header + comparison height + 20px padding
			
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition = elementPosition + window.scrollY - totalOffset;
			
			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth'
			});
		}
	};

	const subscriptionCards = [
		{
			label: 'CURRENT PLAN',
			value: displayFrequency(plan)
		},
		{
			label: 'STATUS',
			value: displayStatus(status)
		},
		{
			label: 'NEXT BILLING',
			value: nextBilling || 'No data Found'
		}
	];

	const chipItems = [
		{ id: 'account-details-section', label: 'Account Details', show: true },
		{ id: 'subscription-section', label: 'Manage Subscription', show: !isSubuser },
		{ id: 'recommended-section', label: 'Recommended Videos', show: !isSubuser },
		{ id: 'studio-overview-section', label: 'Studio Overview', show: isStudioOwner },
		{ id: 'manage-studio-section', label: 'Manage Studio', show: isStudioOwner }
	].filter(item => item.show);

	return (
		<div className="account-v2-page">
			<Facebook />
			<div className="c-tab_cata-text c-my_account-tagline">
				<h4>My ACCOUNT</h4>
			</div>

			<main className="account-v2-shell">
				{chipItems.length > 1 && (
					<div className="account-v2-chips-comparison">
						<div className="account-v2-nav-chips">
							{chipItems.map(item => (
								<button
									key={item.id}
									type="button"
									onClick={() => scrollToSection(item.id)}
									className="account-v2-chip-btn"
								>
									{item.label}
								</button>
							))}
						</div>
					</div>
				)}

				<section id="account-details-section" className="account-v2-panel account-v2-details-panel">
					<div className="account-v2-panel__header">
						<h1>ACCOUNT DETAILS</h1>
						<p>Manage your personal details, login information, and profile photo.</p>
					</div>

					<div className="account-v2-details">
						<ProfileAvatar user={userInfo} fullName={fullName} email={displayEmail} showMeta={false} />

						<div className="account-v2-form-wrap">
							<div className="account-v2-form-grid">
								<AccountField id="account-v2-first-name" label="FIRST NAME" value={firstname} readOnly />
								<AccountField id="account-v2-last-name" label="LAST NAME" value={lastname} readOnly />
								<AccountField id="account-v2-email" label="EMAIL ADDRESS" type="email" value={displayEmail} readOnly />
								<AccountField
									id="account-v2-password"
									label="PASSWORD"
									type="password"
									value={passwordMask}
									disabled
									readOnly
								/>
							</div>
						</div>
					</div>

					<div className="account-v2-details-footer">
						<div className="account-v2-profile__meta account-v2-profile__meta--footer">
							<div className="account-v2-profile__name">{fullName}</div>
							<div className="account-v2-profile__email">{displayEmail || ''}</div>
						</div>

						<div className="account-v2-panel__actions">
							<button
								className="account-v2-primary-button"
								type="button"
								onClick={() => setAccountDetailsModalOpen(true)}
							>
								UPDATE ACCOUNT DETAILS
							</button>
						</div>
					</div>
				</section>

				{!isSubuser && (
					<>
						<section id="subscription-section" className="account-v2-panel account-v2-subscription-panel">
							<div className="account-v2-panel__header">
								<h2>MY SUBSCRIPTION</h2>
								<p>Manage your membership, billing, and upcoming renewals in one place.</p>
							</div>

							<div className="account-v2-subscription-row">
								<div className="account-v2-summary-box">
									{subscriptionCards.map(cardItem => (
										<div className="account-v2-summary-card" key={cardItem.label}>
											<span>{cardItem.label}</span>
											<strong>{cardItem.value}</strong>
										</div>
									))}
								</div>

								<button
									className="account-v2-primary-button"
									type="button"
									onClick={() => setManageSubscriptionModalOpen(true)}
								>
									MANAGE SUBSCRIPTION
								</button>
							</div>
						</section>
						<section id="recommended-section" className="account-v2-panel account-v2-recommended-panel">
							<div className="account-v2-recommended-panel__top">
								<div className="account-v2-panel__header">
									<h2>RECOMMENDED FOR YOU</h2>
									<p>
										{recommendedVideos.length > 0
											? 'Your personalized videos are ready—watch below or retake the quiz anytime.'
											: 'Complete the onboarding quiz to get your recommended plan and training path.'}
									</p>
								</div>
								<button className="account-v2-primary-button" type="button" onClick={handleQuizClick} disabled={isStartingQuiz}>
									{isStartingQuiz ? 'LOADING...' : shouldShowRecommended ? 'START QUIZ' : 'RETAKE QUIZ'}
								</button>
							</div>

							{isLoadingRecommendedPlaylist ? (
								<div className="account-v2-recommended-status">Loading recommendations...</div>
							) : recommendedVideos.length > 0 ? (
								<div className="account-v2-recommended-grid">
									{recommendedVideos.map(video => (
										<div
											className="account-v2-recommended-card"
											key={video?._id}
											onClick={() => handleRecommendedVideoClick(video)}
											role="button"
											tabIndex={0}
											onKeyDown={event => {
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault();
													handleRecommendedVideoClick(video);
												}
											}}
										>
											<VideoCardV2
												video={video}
												customStyles={{ minWidth: '100%', height: '100%' }}
												hoverEffect
												modal={false}
												setModal={() => {}}
												setVideoData={() => {}}
												showAddToPlaylistIcon={false}
												showSaveIcon={false}
											/>
										</div>
									))}
								</div>
							) : null}
						</section>

						{isStudioOwner && (
							<>
								<StudioOverviewMetrics />
								<section id="manage-studio-section" className="account-v2-panel account-v2-manage-studio-panel">
									<div className="account-v2-panel__header">
										<h2>MANAGE STUDIO</h2>
										<p>
											{seatsData
												? `Manage your studio's shared access here.`
												: 'Manage your team seats, invites, and member access in one place.'}
										</p>
									</div>

									<div className="account-v2-studio-users">
										<SubUserAdd subscriptionData={subscriptionData} setSeatsData={setSeatsData} />
									</div>
								</section>
							</>
						)}
					</>
				)}
			</main>

			<MyAccountUpdateDetailsModal
				open={accountDetailsModalOpen}
				onClose={() => {
					if (!isUpdatingAccount) {
						setAccountDetailsModalOpen(false);
					}
				}}
				onSubmit={handleUpdateAccountDetails}
				submitting={isUpdatingAccount}
				isSubuser={isSubuser}
				initialValues={{ firstname, lastname, email: displayEmail }}
				userId={userId}
			/>

			<MyAccountManageSubscriptionModal
				open={manageSubscriptionModalOpen}
				onClose={() => setManageSubscriptionModalOpen(false)}
				onSubscriptionUpdated={handleSubscriptionManagementUpdate}
			/>
		</div>
	);
}

function AccountField({ id, label, type = 'text', value, disabled = false, readOnly = false, onChange }) {
	return (
		<label className="account-v2-field" htmlFor={id}>
			<span>{label}</span>
			<input id={id} type={type} value={value} disabled={disabled} readOnly={readOnly} onChange={onChange} />
		</label>
	);
}

function CircularProgressWithLabel(props) {
	return (
		<Box sx={{ position: 'relative', display: 'inline-flex' }}>
			<CircularProgress variant="determinate" {...props} />
			<Box
				sx={{
					top: 0,
					left: 0,
					bottom: 0,
					right: 0,
					position: 'absolute',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					'& .MuiTypography-root': {
						fontFamily: 'Arial',
						color: 'white',
						fontWeight: 700
					}
				}}
			>
				<Typography variant="caption" component="div" color="text.secondary">
					{`${Math.round(props.value)}%`}
				</Typography>
			</Box>
		</Box>
	);
}

function ProfileAvatar({ user, fullName, email, showMeta = true }) {
	const cookies = new Cookies();
	const dispatch = useDispatch();
	const setUserData = useSetAtom(userInfoAtom);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [isRemovingImage, setIsRemovingImage] = useState(false);
	const [removeConfirmModalOpen, setRemoveConfirmModalOpen] = useState(false);
	const [imageURL, setImageURL] = useState(null);

	const getProfileImage = useCallback(() => {
		if (!user?.userId) return;

		axiosConfig
			.get(`${import.meta.env.VITE_APP_API_URL}/uploadthing`, {
				params: {
					userId: user.userId
				}
			})
			.then(res => {
				setImageURL(res.data?.url);
			})
			.catch(err => {
				console.log(err);
			});
	}, [user?.userId]);

	useEffect(() => {
		getProfileImage();
	}, [getProfileImage]);

	const onDrop = useCallback(
		async acceptedFiles => {
			if (!acceptedFiles.length || !user?.userId) return;

			const file = acceptedFiles[0];
			setUploadProgress(15);
			setIsUploading(true);

			try {
				const response = await fileUploadApi(file);
				setUploadProgress(70);

				await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/uploadthing`, {
					userId: user.userId,
					imageURL: response.data.cdnLink
				});

				getProfileImage();
				const userUpdated = {
					...user,
					image: response.data.cdnLink
				};

				cookies.set('user', userUpdated);
				localStorage.setItem('userData', JSON.stringify(userUpdated));
				dispatch(setUserInfo(userUpdated));
				setUserData(prev => ({
					...prev,
					user: userUpdated
				}));

				setUploadProgress(100);
				Toast.success('Profile image successfully uploaded');
			} catch (error) {
				Toast.error('Failed to upload image. Please try again later.');
			} finally {
				setIsUploading(false);
			}
		},
		[cookies, dispatch, getProfileImage, setUserData, user]
	);

	const { getRootProps, getInputProps, open } = useDropzone({
		onDrop,
		disabled: isUploading || isRemovingImage,
		noClick: true,
		accept: {
			'image/gif': ['.gif'],
			'image/jpeg': ['.jpe', '.jpg', '.jpeg'],
			'image/svg+xml': ['.svg'],
			'image/png': ['.png']
		}
	});

	const handleUploadClick = () => {
		if (isRemovingImage || isUploading) return;
		open();
	};

	const handleRemoveImage = async () => {
		const hasImage = Boolean(imageURL ?? user?.image);
		if (!user?.userId || !hasImage || isUploading || isRemovingImage) return;

		setIsRemovingImage(true);

		try {
			await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/uploadthing`, {
				userId: user.userId,
				imageURL: ''
			});

			setImageURL('');

			const userUpdated = {
				...user,
				image: ''
			};

			cookies.set('user', userUpdated);
			localStorage.setItem('userData', JSON.stringify(userUpdated));
			dispatch(setUserInfo(userUpdated));
			setUserData(prev => ({
				...prev,
				user: userUpdated
			}));

			Toast.success('Profile image removed successfully!');
		} catch (error) {
			Toast.error('Failed to remove image. Please try again later.');
		} finally {
			setIsRemovingImage(false);
		}
	};

	return (
		<div className="account-v2-profile">
			<div className="account-v2-profile__top">
				<button className="account-v2-avatar-button" type="button" {...getRootProps()} onClick={handleUploadClick}>
					<input {...getInputProps()} />
					<UserImage height={170} width={170} imageURL={imageURL || user?.image} />
					{isUploading && (
						<span className="account-v2-avatar-progress">
							<CircularProgressWithLabel value={uploadProgress} />
						</span>
					)}
				</button>

				<div className="account-v2-profile__photo-actions">
					<button
						type="button"
						className="account-v2-primary-button account-v2-profile__photo-action-button account-v2-profile__replace-photo-button"
						onClick={handleUploadClick}
						disabled={isUploading || isRemovingImage}
					>
						REPLACE
					</button>

					<button
						type="button"
						className="account-v2-primary-button account-v2-profile__photo-action-button account-v2-profile__remove-photo-button"
						onClick={() => setRemoveConfirmModalOpen(true)}
						disabled={isUploading || isRemovingImage || !(imageURL || user?.image)}
					>
						{isRemovingImage ? 'REMOVING...' : 'REMOVE'}
					</button>
				</div>
			</div>

			{showMeta && (
				<div className="account-v2-profile__meta">
					<div className="account-v2-profile__name">{fullName}</div>
					<div className="account-v2-profile__email">{email ? `${email}` : ''}</div>
				</div>
			)}

			{removeConfirmModalOpen && (
				<div className="manage-subscription-modal__overlay manage-subscription-modal__overlay--active">
					<div className="manage-subscription-modal__panel manage-subscription-modal__panel--confirm">
						<h4>Remove Profile Image</h4>
						<p>Are you sure you want to remove your profile image?</p>
						<div className="manage-subscription-modal__confirm-actions">
							<button
								type="button"
								className="manage-subscription-modal__confirm-cancel"
								onClick={() => setRemoveConfirmModalOpen(false)}
								disabled={isRemovingImage}
							>
								Cancel
							</button>
							<button
								type="button"
								className="manage-subscription-modal__confirm-submit"
								onClick={async () => {
									setRemoveConfirmModalOpen(false);
									await handleRemoveImage();
								}}
								disabled={isRemovingImage}
							>
								{isRemovingImage ? 'Removing...' : 'Remove Image'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default MyAccountV2;
